import HPOCategorization from '@/features/terminology/hpo/HPOCategorization'
import HPOSearch from '@/features/terminology/hpo/HPOSearchClient'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { clearFatherData, clearMotherData, updateFatherData, updateMotherData } from '@/store/slices/formSlice'
import { FormSection } from '@/types/FormSectionInterface'
import {
  BiologicalParentData,
  CategorizedHPOOption,
  CollectionType,
  HPOOption,
  PhenotypicTrait,
} from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import validator from 'validator'

interface BiologicalParentInformationProps {
  initialData?: Record<string, unknown>
  onDataChange?: (data: BiologicalParentData) => void
  onComplete?: (isComplete: boolean) => void
  parentType?: 'mother' | 'father'
}

// Common form content component to eliminate duplication
const BiologicalParentFormContent: React.FC<{
  parentData: BiologicalParentData
  parentType?: 'mother' | 'father'
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>) => void
  onRadioChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onHPOTermsChange: (selectedOptions: HPOOption[]) => void
  onCategoryChange: (termId: string, newCategory: 'Key' | 'Additional' | 'Excluded') => void
  onICD10TermsChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  emailError: string
  phoneError: string
  onPhoneFormat: (value: string) => void
}> = ({
  parentData,
  parentType,
  onInputChange,
  onRadioChange,
  onHPOTermsChange,
  onCategoryChange,
  onICD10TermsChange,
  emailError,
  phoneError,
}) => {
  const parentLabel = parentType === 'mother' ? 'Mother' : parentType === 'father' ? 'Father' : 'Parent'

  return (
    <div className="space-y-6">
      <h3 className="section-header border-b border-gray-200 pb-2">{parentLabel} Information</h3>

      {/* Parent Type Selection - Only show if not specified by prop */}
      {!parentType && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-700">
            Relationship <span className="text-red-500">*</span>
          </h4>

          <div className="flex space-x-6">
            <label className="flex items-center">
              <input
                type="radio"
                name="parentType"
                value="mother"
                checked={parentData.parentType === 'mother'}
                onChange={onRadioChange}
                className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="form-label">Mother</span>
            </label>

            <label className="flex items-center">
              <input
                type="radio"
                name="parentType"
                value="father"
                checked={parentData.parentType === 'father'}
                onChange={onRadioChange}
                className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="form-label">Father</span>
            </label>
          </div>
        </div>
      )}

      {/* Parent Information */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-700">Basic Information</h4>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="parentFirstName" className="form-label mb-1 block">
              {parentLabel}'s First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="parentFirstName"
              name="parentFirstName"
              value={parentData.parentFirstName}
              onChange={onInputChange}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={`Enter ${parentLabel.toLowerCase()}'s first name`}
            />
          </div>

          <div>
            <label htmlFor="parentLastName" className="form-label mb-1 block">
              {parentLabel}'s Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="parentLastName"
              name="parentLastName"
              value={parentData.parentLastName}
              onChange={onInputChange}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={`Enter ${parentLabel.toLowerCase()}'s last name`}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="parentDateOfBirth" className="form-label mb-1 block">
              {parentLabel}'s Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="parentDateOfBirth"
              name="parentDateOfBirth"
              value={parentData.parentDateOfBirth}
              onChange={onInputChange}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="parentMRN" className="form-label mb-1 block">
              {parentLabel}'s MRN <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="parentMRN"
              name="parentMRN"
              value={parentData.parentMRN}
              onChange={onInputChange}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter Medical Record Number"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="parentPhone" className="form-label mb-1 block">
              {parentLabel}'s Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="parentPhone"
              name="parentPhone"
              value={parentData.parentPhone}
              onChange={onInputChange}
              required
              className={`w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                phoneError
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
              }`}
              placeholder="Enter phone number"
            />
            {phoneError && <p className="mt-1 text-sm text-red-600">{phoneError}</p>}
          </div>

          <div>
            <label htmlFor="parentEmail" className="form-label mb-1 block">
              {parentLabel}'s Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="parentEmail"
              name="parentEmail"
              value={parentData.parentEmail}
              onChange={onInputChange}
              required
              className={`w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                emailError
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
              }`}
              placeholder="Enter email address"
            />
            {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
          </div>
        </div>
      </div>

      {/* Phenotypic Trait */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-700">
          Phenotypic Trait <span className="text-red-500">*</span>
        </h4>

        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              type="radio"
              name="phenotypicTrait"
              value={PhenotypicTrait.ClinicallyUnaffected}
              checked={parentData.phenotypicTrait === PhenotypicTrait.ClinicallyUnaffected}
              onChange={onInputChange}
              className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="form-label">Clinically Unaffected</span>
          </label>

          <label className="flex items-center">
            <input
              type="radio"
              name="phenotypicTrait"
              value={PhenotypicTrait.ClinicallyAffected}
              checked={parentData.phenotypicTrait === PhenotypicTrait.ClinicallyAffected}
              onChange={onInputChange}
              className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="form-label">Clinically Affected</span>
          </label>
        </div>
      </div>

      {/* HPO Terms */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-700">Phenotype</h4>

        <div>
          <label htmlFor="parentHPOTerms" className="form-label mb-1 block">
            HPO Terms
          </label>
          <HPOSearch
            id="parentHPOTerms"
            value={parentData.hpoTerms}
            onChange={onHPOTermsChange}
            placeholder="Search for HPO terms..."
            maxSelections={10}
          />
          <HPOCategorization
            categorizedTerms={parentData.hpoTerms}
            onCategoryChange={onCategoryChange}
            title="Categorize HPO Terms"
          />
        </div>

        <div>
          <label htmlFor="parentICD10Terms" className="form-label mb-1 block">
            ICD-10 Diagnosis Codes
          </label>
          <textarea
            id="parentICD10Terms"
            name="icd10Terms"
            value={parentData.icd10Terms}
            onChange={onICD10TermsChange}
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter ICD-10 diagnosis codes"
            rows={3}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-700">
          Collection Type <span className="text-red-500">*</span>
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <label
            className={`flex cursor-pointer items-center rounded-lg border-2 p-4 transition-colors ${
              parentData.collectionType === CollectionType.WholeBloodEDTA
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input
              type="radio"
              name="collectionType"
              value={CollectionType.WholeBloodEDTA}
              checked={parentData.collectionType === CollectionType.WholeBloodEDTA}
              onChange={onInputChange}
              className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="form-label ml-3">Whole Blood (EDTA)</span>
          </label>
          <label
            className={`flex cursor-pointer items-center rounded-lg border-2 p-4 transition-colors ${
              parentData.collectionType === CollectionType.Saliva
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input
              type="radio"
              name="collectionType"
              value={CollectionType.Saliva}
              checked={parentData.collectionType === CollectionType.Saliva}
              onChange={onInputChange}
              className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="form-label ml-3">Saliva (confirmatory testing only)</span>
          </label>
        </div>
      </div>
    </div>
  )
}

const BiologicalParentInformation = forwardRef<FormSection, BiologicalParentInformationProps>(
  ({ initialData, onDataChange, onComplete, parentType }, ref) => {
    const dispatch = useAppDispatch()
    const { formData } = useAppSelector((state) => state.form)
    const isInitialized = useRef(false)
    const [selectedParentType, setSelectedParentType] = useState<'mother' | 'father'>(
      (initialData?.parentType as 'mother' | 'father') || parentType || 'mother'
    )

    // Initialize data from props or with empty values
    const getInitialData = useCallback((): BiologicalParentData => {
      return {
        parentType: (initialData?.parentType as 'mother' | 'father') || parentType || 'mother',
        parentFirstName: String(initialData?.parentFirstName || ''),
        parentLastName: String(initialData?.parentLastName || ''),
        parentDateOfBirth: String(initialData?.parentDateOfBirth || ''),
        parentMRN: String(initialData?.parentMRN || ''),
        parentPhone: String(initialData?.parentPhone || ''),
        parentEmail: String(initialData?.parentEmail || ''),
        phenotypicTrait: (initialData?.phenotypicTrait as PhenotypicTrait) || PhenotypicTrait.Unknown,
        hpoTerms: Array.isArray(initialData?.hpoTerms) ? (initialData.hpoTerms as CategorizedHPOOption[]) : [],
        icd10Terms: String(initialData?.icd10Terms || ''),
        collectionType: (initialData?.collectionType as CollectionType) || CollectionType.Unknown,
      }
    }, [initialData, parentType])

    // Memoize the current parent data to prevent unnecessary re-renders
    const currentParentData = useMemo(() => {
      if (parentType === 'mother') return formData.mother
      if (parentType === 'father') return formData.father
      return selectedParentType === 'mother' ? formData.mother : formData.father
    }, [parentType, selectedParentType, formData.mother, formData.father])

    // Use Redux state as the source of truth, fallback to props for initial data
    const parentData = currentParentData || getInitialData()
    const [emailError, setEmailError] = useState('')
    const [phoneError, setPhoneError] = useState('')

    // Implement the FormSection interface
    useImperativeHandle(ref, () => ({
      isSectionComplete: () => {
        return FormCompletionChecker.isBiologicalParentComplete(parentData)
      },
      renderSection: () => {
        return (
          <BiologicalParentFormContent
            parentData={parentData}
            parentType={parentType}
            onInputChange={handleInputChange}
            onRadioChange={handleRadioChange}
            onHPOTermsChange={handleHPOTermsChange}
            onCategoryChange={handleCategoryChange}
            onICD10TermsChange={handleICD10TermsChange}
            emailError={emailError}
            phoneError={phoneError}
            onPhoneFormat={(value) => {
              const updateData = { parentPhone: value }

              // Update Redux state based on parent type
              if (parentType === 'mother') {
                dispatch(updateMotherData(updateData))
              } else if (parentType === 'father') {
                dispatch(updateFatherData(updateData))
              } else {
                // For dynamic parent type selection, determine which to update
                const currentParentType = parentData.parentType
                if (currentParentType === 'mother') {
                  dispatch(updateMotherData(updateData))
                } else if (currentParentType === 'father') {
                  dispatch(updateFatherData(updateData))
                }
              }

              // Also call the callback for backward compatibility
              const newData = { ...parentData, ...updateData }
              onDataChange?.(newData)
              const error = validator.isMobilePhone(value)
              setPhoneError(error ? '' : 'Invalid phone number')
            }}
          />
        )
      },
      renderSectionInfo: () => {
        return <div></div>
      },
    }))

    // Initialize with data only once
    useEffect(() => {
      if (isInitialized.current) return

      // Check if parent data is empty (all fields are empty strings or default values)
      const isParentDataEmpty =
        !currentParentData ||
        (!currentParentData.parentFirstName &&
          !currentParentData.parentLastName &&
          !currentParentData.parentMRN &&
          currentParentData.phenotypicTrait === PhenotypicTrait.Unknown &&
          currentParentData.collectionType === CollectionType.Unknown)

      // Only initialize Redux state if there's no existing meaningful data and we have meaningful initial data
      if (isParentDataEmpty && initialData && Object.keys(initialData).length > 0) {
        const initialDataToUse = getInitialData()
        // Update Redux state with initial data
        if (parentType === 'mother') {
          dispatch(updateMotherData(initialDataToUse))
        } else if (parentType === 'father') {
          dispatch(updateFatherData(initialDataToUse))
        } else {
          // For dynamic parent type selection, use the parentType from initialData
          const dataParentType = initialDataToUse.parentType
          if (dataParentType === 'mother') {
            dispatch(updateMotherData(initialDataToUse))
          } else if (dataParentType === 'father') {
            dispatch(updateFatherData(initialDataToUse))
          }
        }
        onDataChange?.(initialDataToUse)
      }

      isInitialized.current = true
    }, [currentParentData, initialData, onDataChange, getInitialData, dispatch, parentType])

    // Handle completion status updates when data changes
    useEffect(() => {
      const dataToCheck = currentParentData || getInitialData()
      const isComplete =
        dataToCheck.parentFirstName.trim() !== '' &&
        dataToCheck.parentLastName.trim() !== '' &&
        dataToCheck.parentDateOfBirth.trim() !== '' &&
        dataToCheck.parentMRN.trim() !== '' &&
        dataToCheck.parentPhone.trim() !== '' &&
        dataToCheck.parentEmail.trim() !== '' &&
        dataToCheck.phenotypicTrait !== PhenotypicTrait.Unknown &&
        dataToCheck.collectionType.trim() !== '' &&
        validator.isEmail(dataToCheck.parentEmail) &&
        validator.isMobilePhone(dataToCheck.parentPhone)
      onComplete?.(isComplete)
    }, [currentParentData, onComplete, getInitialData])

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target
      let updateData = { [name]: value }

      if (name === 'parentEmail') {
        const error = validator.isEmail(value)
        setEmailError(error ? '' : 'Invalid email address')
        updateData = { ...updateData, parentEmail: value }
      } else if (name === 'parentPhone') {
        const error = validator.isMobilePhone(value)
        setPhoneError(error ? '' : 'Invalid phone number')
        updateData = { ...updateData, parentPhone: value }
      }

      // Update Redux state based on parent type
      if (parentType === 'mother') {
        dispatch(updateMotherData(updateData))
      } else if (parentType === 'father') {
        dispatch(updateFatherData(updateData))
      } else {
        // For dynamic parent type selection, determine which to update
        const currentParentType = parentData.parentType
        if (currentParentType === 'mother') {
          dispatch(updateMotherData(updateData))
        } else if (currentParentType === 'father') {
          dispatch(updateFatherData(updateData))
        }
      }

      // Also call the callback for backward compatibility
      const newData = { ...parentData, ...updateData }
      onDataChange?.(newData)

      // Check completion status after data change
      const newIsComplete =
        newData.parentFirstName.trim() !== '' &&
        newData.parentLastName.trim() !== '' &&
        newData.parentDateOfBirth.trim() !== '' &&
        newData.parentMRN.trim() !== '' &&
        newData.parentPhone.trim() !== '' &&
        newData.parentEmail.trim() !== '' &&
        newData.phenotypicTrait !== PhenotypicTrait.Unknown &&
        newData.collectionType.trim() !== '' &&
        validator.isEmail(newData.parentEmail) &&
        validator.isMobilePhone(newData.parentPhone)
      onComplete?.(newIsComplete)
    }

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      const newParentType = value as 'mother' | 'father'
      const updateData = { parentType: newParentType }

      // Update Redux state based on parent type
      if (parentType === 'mother') {
        dispatch(updateMotherData(updateData))
      } else if (parentType === 'father') {
        dispatch(updateFatherData(updateData))
      } else {
        // Set the locally selected parent type for dynamic mode
        setSelectedParentType(newParentType)
        // For dynamic parent type selection, clear the previously selected parent's data
        const currentParentType = parentData.parentType

        // If switching from one parent to another, clear the old parent's data
        if (currentParentType !== newParentType) {
          if (currentParentType === 'mother') {
            dispatch(clearMotherData())
          } else if (currentParentType === 'father') {
            dispatch(clearFatherData())
          }
        }

        // Update the new parent's data
        if (newParentType === 'mother') {
          dispatch(updateMotherData(updateData))
        } else if (newParentType === 'father') {
          dispatch(updateFatherData(updateData))
        }
      }

      // Also call the callback for backward compatibility
      const newData = { ...parentData, ...updateData }
      onDataChange?.(newData)
    }

    const handleHPOTermsChange = (selectedOptions: HPOOption[]) => {
      // Convert HPOOption[] to CategorizedHPOOption[] with default 'Key' category
      const categorizedOptions: CategorizedHPOOption[] = selectedOptions.map((option) => ({
        ...option,
        category: 'Key' as const,
      }))

      const updateData = { hpoTerms: categorizedOptions }

      // Update Redux state based on parent type
      if (parentType === 'mother') {
        dispatch(updateMotherData(updateData))
      } else if (parentType === 'father') {
        dispatch(updateFatherData(updateData))
      } else {
        // For dynamic parent type selection, determine which to update
        const currentParentType = parentData.parentType
        if (currentParentType === 'mother') {
          dispatch(updateMotherData(updateData))
        } else if (currentParentType === 'father') {
          dispatch(updateFatherData(updateData))
        }
      }

      // Also call the callback for backward compatibility
      const newData = { ...parentData, ...updateData }
      onDataChange?.(newData)
    }

    const handleCategoryChange = (termId: string, newCategory: 'Key' | 'Additional' | 'Excluded') => {
      const updatedTerms = parentData.hpoTerms.map((term) =>
        term.id === termId ? { ...term, category: newCategory } : term
      )

      const updateData = { hpoTerms: updatedTerms }

      // Update Redux state based on parent type
      if (parentType === 'mother') {
        dispatch(updateMotherData(updateData))
      } else if (parentType === 'father') {
        dispatch(updateFatherData(updateData))
      } else {
        // For dynamic parent type selection, determine which to update
        const currentParentType = parentData.parentType
        if (currentParentType === 'mother') {
          dispatch(updateMotherData(updateData))
        } else if (currentParentType === 'father') {
          dispatch(updateFatherData(updateData))
        }
      }

      // Also call the callback for backward compatibility
      const newData = { ...parentData, ...updateData }
      onDataChange?.(newData)
    }

    const handleICD10TermsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target
      const updateData = { icd10Terms: value }

      // Update Redux state based on parent type
      if (parentType === 'mother') {
        dispatch(updateMotherData(updateData))
      } else if (parentType === 'father') {
        dispatch(updateFatherData(updateData))
      } else {
        // For dynamic parent type selection, determine which to update
        const currentParentType = parentData.parentType
        if (currentParentType === 'mother') {
          dispatch(updateMotherData(updateData))
        } else if (currentParentType === 'father') {
          dispatch(updateFatherData(updateData))
        }
      }

      // Also call the callback for backward compatibility
      const newData = { ...parentData, ...updateData }
      onDataChange?.(newData)
    }

    // Render the form using the common component
    return (
      <BiologicalParentFormContent
        parentData={parentData}
        parentType={parentType}
        onInputChange={handleInputChange}
        onRadioChange={handleRadioChange}
        onHPOTermsChange={handleHPOTermsChange}
        onCategoryChange={handleCategoryChange}
        onICD10TermsChange={handleICD10TermsChange}
        emailError={emailError}
        phoneError={phoneError}
        onPhoneFormat={(value) => {
          const updateData = { parentPhone: value }

          // Update Redux state based on parent type
          if (parentType === 'mother') {
            dispatch(updateMotherData(updateData))
          } else if (parentType === 'father') {
            dispatch(updateFatherData(updateData))
          } else {
            // For dynamic parent type selection, determine which to update
            const currentParentType = parentData.parentType
            if (currentParentType === 'mother') {
              dispatch(updateMotherData(updateData))
            } else if (currentParentType === 'father') {
              dispatch(updateFatherData(updateData))
            }
          }

          // Also call the callback for backward compatibility
          const newData = { ...parentData, ...updateData }
          onDataChange?.(newData)
          const error = validator.isMobilePhone(value)
          setPhoneError(error ? '' : 'Invalid phone number')
        }}
      />
    )
  }
)

export default BiologicalParentInformation
